<template>
  <expansion-panel
    key="expansion-panel-total-price"
    :title="$t('offer-manager.create.step-total-price.title')"
    :readonly="panelReadonly"
    :hideDivider="panelHideDivider">
    <template v-slot:panel-content>
      <m-form-row :label="$t('offer-manager.create.step-total-price.lbl-rate-price')">
        <mapit-input
          uid="offerTotalPriceRate"
          :value="parseFloat(totalPrice.rate || 0).toFixed(2)"
          :placeholder="$t('offer-manager.create.step-total-price.lbl-rate-price')"
          :type="InputTypes.PRICE"
          :isReadonly="true"
          :disabled="disabled" />
      </m-form-row>
      <m-form-row>
        <template v-slot:custom-label>
          <div class="d-flex align-center text-left">
            <span>{{ $t('offer-manager.create.step-total-price.lbl-total-discounts-price') }}</span>
            <div @click="openCloseInfoDialog('total-discounts-price')">
              <v-icon dense class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" />
            </div>
          </div>
        </template>
        <template v-slot:default>
          <price-field
            data-cy="offerTotalPriceDiscounts"
            :value="totalPriceDiscounts"
            :placeholder="$t('offer-manager.create.step-total-price.lbl-total-discounts-price')"
            :readonly="true" />
        </template>
      </m-form-row>
      <m-form-row>
        <template v-slot:custom-label>
          <div class="d-flex align-center text-left">
            {{ $t('offer-manager.create.step-total-price.lbl-additional-discount') }}
            <div @click="openCloseInfoDialog('additional-discount')">
              <v-icon dense class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" />
            </div>
          </div>
        </template>
        <template v-slot:default>
          <mapit-input
            uid="offerTotalPriceAdditionalDiscountPriceRate"
            :value="additionalDiscountPriceRate"
            :placeholder="$t('offer-manager.create.step-total-price.lbl-additional-discount-placeholder')"
            :type="InputTypes.PRICE"
            @changeValue="value => updateOffer('setAdditionalDiscountPriceRate', value)"
            :rules="[...additionalDiscountRules, ...additionalDiscountRulesExtra]"
            :disabled="!vehiclePriceRateCCAA || disabled"
            :negativeValue="true"
            @validateForm="validateForm" />
        </template>
      </m-form-row>

      <m-form-row
        :label="$t('offer-manager.create.step-total-price.lbl-total-without-delivered-vehicle')"
        v-if="totalPrice.priceWithoutDelivered">
        <mapit-input
          uid="offerTotalPriceRateWithoutDelivered"
          :value="totalPrice.priceWithoutDelivered"
          :placeholder="$t('offer-manager.create.step-total-price.lbl-total-without-delivered-vehicle')"
          :type="InputTypes.PRICE"
          :isReadonly="true"
          :disabled="disabled" />
      </m-form-row>
      <v-divider v-if="totalPrice.priceWithoutDelivered" class="py-1" />

      <m-form-row
        :label="$t('offer-manager.create.step-total-price.lbl-total-without-delivered-vehicle-amount')"
        v-if="totalPrice.deliveredAmount">
        <mapit-input
          uid="offerTotalPriceDeliveredAmount"
          :value="totalPrice.deliveredAmount"
          :placeholder="$t('offer-manager.create.step-total-price.lbl-total-without-delivered-vehicle-amount')"
          :type="InputTypes.PRICE"
          :isReadonly="true"
          :negativeValue="true"
          :disabled="disabled" />
      </m-form-row>

      <m-form-row :label="$t('offer-manager.create.step-total-price.lbl-total-price')" highlighted>
        <mapit-input
          uid="offerTotalPriceTotal"
          :value="parseFloat(totalPrice.price || 0).toFixed(2)"
          :isReadonly="true"
          :placeholder="$t('offer-manager.create.step-total-price.lbl-total-price')"
          :type="InputTypes.PRICE"
          :disabled="disabled" />
      </m-form-row>

      <mapit-dialog :dialog="dialog" @close="openCloseInfoDialog">
        <template v-slot:header-text>
          <span>{{ $t(`offer-manager.create.step-total-price.${infoVariant}.title`) }}</span>
        </template>
        <template v-slot:body>
          <div class="mb-6" v-html="$t(`offer-manager.create.step-total-price.${infoVariant}.description`)" />
        </template>
      </mapit-dialog>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {computed, defineComponent, ref} from '@vue/composition-api'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import MapitInput from '@/components/general/MapitInput.vue'
import PriceField from '@/components/general/PriceField.vue'
// @ts-ignore
import {additionalDiscountRules} from '@/helpers/offer-manager-rules'
import {InputTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'TotalPriceData',
  components: {ExpansionPanel, MapitInput, MapitDialog, PriceField},
  props: {
    hideDetails: {
      type: Boolean,
      default: false,
    },
    panelReadonly: {
      type: Boolean,
      default: false,
    },
    panelHideDivider: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const dialog = ref(false)
    const infoVariant = ref('total-discounts-price')

    const {
      getVehiclePriceRateCCAA: vehiclePriceRateCCAA,
      getVehicleDiscountPriceRatePercent: vehicleDiscountPriceRatePercent,
      getTotalPrice: totalPrice,
      getAdditionalDiscountPriceRate: additionalDiscountPriceRate,
    } = mapGetters(root.$store.getters)

    const additionalDiscountRulesExtra = computed(() => [
      (value: any) => {
        if (!value) return true
        const fValue =
          typeof value === 'string' ? parseFloat(value.replace('.', '').replace('-', '').replace(',', '.')) : value
        return totalPrice.value.rate - totalPrice.value.discount - fValue >= 0 || root.$t('error.final-discount')
      },
    ])

    const totalPriceDiscounts = computed(() => {
      const floatValue = parseFloat(totalPrice.value.discount || 0) * -1
      return floatValue.toFixed(2)
    })

    function updateOffer(action: string, value: any) {
      root.$store.dispatch(action, value)
      validateForm()
    }

    function validateForm() {
      emit('validateForm')
    }

    function openCloseInfoDialog(variant: string) {
      dialog.value = !dialog.value
      infoVariant.value = variant
    }

    return {
      InputTypes,
      additionalDiscountRules,
      dialog,
      infoVariant,
      additionalDiscountRulesExtra,
      vehiclePriceRateCCAA,
      vehicleDiscountPriceRatePercent,
      totalPrice,
      additionalDiscountPriceRate,
      totalPriceDiscounts,
      updateOffer,
      validateForm,
      openCloseInfoDialog,
    }
  },
})
</script>
